import { html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants, TABS } from './sector-attribution.constants.js';
import type { SectorAttributionTab } from './sector-attribution.constants.js';
import type {
  SectorAttributionContentObject,
  SectorAttributionPeriod,
} from './sector-attribution.model.js';
import style from './sector-attribution.scss?inline';
import { prepareItemsForChart } from './sector-attribution.utils.js';

/**
 * @tag pop-sector-attribution-component
 * @attribute { SectorAttributionContentObject } [data-component-object] SectorAttribution content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorAttributionComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorAttribution content object
   */
  componentObject?: SectorAttributionContentObject;

  /**
   * @internal
   * Storing this as state is being used to enable the workaround we are using
   * to ensure the chart renders consistently when switching between tabs.
   */
  @state()
  selectedTab: SectorAttributionTab = TABS.QUARTERLY;

  private renderTopSection() {
    return html`
      <h3
        class="sector-attribution-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderAsOfDate(selectedDate: string) {
    return html`
      <beacon-control>
        <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}">
          ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
        </label>
        <select
          id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
          @change="To be implemented in a future MR"
        >
          <option value="${selectedDate}">${selectedDate}</option>
        </select>
      </beacon-control>
    `;
  }

  private renderControlsSection(asOfDate?: string | null) {
    if (!asOfDate) {
      return html``;
    }

    return html`
      <div class="sector-attribution-controls b-margin-block-end--micro--jumbo">
        ${this.renderAsOfDate(asOfDate)}
      </div>
    `;
  }

  private renderPeriodContent(periodData?: SectorAttributionPeriod | null) {
    if (!periodData) {
      return html`<pop-no-data-available-component
        .message="${Constants.NO_DATA_MESSAGE}"
      ></pop-no-data-available-component>`;
    }

    if (!periodData.items || periodData.items.length === 0) {
      return html`
        ${this.renderControlsSection(periodData.effectiveDate)}
        <pop-no-data-available-component .message="${Constants.NO_DATA_MESSAGE}"></pop-no-data-available>
      `;
    }

    return html`
      ${this.renderControlsSection(periodData.effectiveDate)}
      <pop-sector-attribution-chart-component
        data-component-object="${JSON.stringify({
          items: prepareItemsForChart(periodData.items),
        })}"
      ></pop-sector-attribution-chart-component>
      <pop-sector-attribution-table-component
        data-component-object="${JSON.stringify({
          items: periodData.items,
        })}"
      ></pop-sector-attribution-table-component>
      ${this.renderBenchmark('benchmark for currently selected date')}
    `;
  }

  private renderQuarterlyContent() {
    return this.renderPeriodContent(this.componentObject?.quarterly);
  }

  private renderYTDContent() {
    return this.renderPeriodContent(this.componentObject?.yearToDate);
  }

  private renderPriorCalendarYearContent() {
    return this.renderPeriodContent(this.componentObject?.priorCalendarYear);
  }

  private renderBenchmark(benchmarkName: string) {
    return html`<div class="b-margin-block-start--micro--jumbo">
      <p>Benchmark - ${benchmarkName}</p>
    </div>`;
  }

  private renderDisclaimersSection() {
    return html`<div class="b-margin-block-start--micro--jumbo">
      <p>Disclaimers go here</p>
    </div>`;
  }

  private renderTabsSection() {
    return html`
      <beacon-tabs variant="secondary" tab-type="standard">
        <beacon-tab
          slot="tab-children"
          label="${Constants.QUARTERLY_TAB_LABEL}"
          @click="${() => {
            this.selectedTab = TABS.QUARTERLY;
          }}"
        ></beacon-tab>
        <beacon-tab
          slot="tab-children"
          label="${Constants.YTD_TAB_LABEL}"
          @click="${() => {
            this.selectedTab = TABS.YTD;
          }}"
        ></beacon-tab>
        <beacon-tab
          slot="tab-children"
          label="${Constants.PRIOR_CALENDAR_YEAR_TAB_LABEL}"
          @click="${() => {
            this.selectedTab = TABS.PRIOR_CALENDAR_YEAR;
          }}"
        ></beacon-tab>
        <beacon-tab-panel slot="tab-panel">
          <!--
               The condition checked here is being used as a workaround to ensure
               that the chart renders correctly.
              -->
          ${this.selectedTab === TABS.QUARTERLY
            ? this.renderQuarterlyContent()
            : html``}
        </beacon-tab-panel>
        <beacon-tab-panel slot="tab-panel">
          <!--
               The condition checked here is being used as a workaround to ensure
               that the chart renders correctly.
              -->
          ${this.selectedTab === TABS.YTD ? this.renderYTDContent() : html``}
        </beacon-tab-panel>
        <beacon-tab-panel slot="tab-panel">
          <!--
               The condition checked here is being used as a workaround to ensure
               that the chart renders correctly.
              -->
          ${this.selectedTab === TABS.PRIOR_CALENDAR_YEAR
            ? this.renderPriorCalendarYearContent()
            : html``}
        </beacon-tab-panel>
      </beacon-tabs>
    `;
  }

  render() {
    return this.renderComponent(
      html`<section class="b-container">
        ${this.renderTopSection()} ${this.renderTabsSection()}
        ${this.renderDisclaimersSection()}
      </section> `,
    );
  }
}
